import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';

export interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'children' | 'type'> {
  id: string;
  label: ReactNode;
  helpText?: ReactNode;
  errorText?: ReactNode;
  trailing?: ReactNode;
  showLabel?: string;
  hideLabel?: string;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField(
    {
      id,
      label,
      helpText,
      errorText,
      trailing,
      className,
      showLabel = 'Show',
      hideLabel = 'Hide',
      ...rest
    },
    ref
  ) {
    const [revealed, setRevealed] = useState(false);
    const helpId = helpText ? `${id}-help` : undefined;
    const errorId = errorText ? `${id}-error` : undefined;
    const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;
    return (
      <div className={['pn-field', className].filter(Boolean).join(' ')}>
        <label htmlFor={id} className="pn-field-label">
          {label}
        </label>
        <div className="pn-pwd-field">
          <input
            ref={ref}
            id={id}
            type={revealed ? 'text' : 'password'}
            className="pn-field-input pn-pwd-field-input"
            aria-describedby={describedBy}
            aria-invalid={errorText ? true : undefined}
            {...rest}
          />
          <button
            type="button"
            className="pn-pwd-toggle"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? hideLabel : showLabel}
            aria-pressed={revealed}
          >
            {revealed ? hideLabel : showLabel}
          </button>
        </div>
        {trailing}
        {helpText ? (
          <span id={helpId} className="pn-field-help">
            {helpText}
          </span>
        ) : null}
        {errorText ? (
          <span id={errorId} className="pn-field-error" role="alert">
            {errorText}
          </span>
        ) : null}
      </div>
    );
  }
);
